import { useState, useEffect } from "react";
import {
  Modal,
  BlockStack,
  Text,
  Box,
  DropZone,
  Button,
} from "@medentic/polaris-react";
import { XIcon } from "@medentic/polaris-icons";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showToast } from "@shared/lib";
import { DatePickerField } from "@shared/ui";
import { ImageDetailType, TestimonialItem } from "@features/events/types";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { GenerativeTextField } from "@shared/ui";
import { WebsiteField } from "@shared/ui";
import i18n from "@shared/i18n/i18n";

interface UploadResponseImage {
  src: string;
  width: number;
  height: number;
  type: string;
  size: number;
}

interface AddEditTestimonialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (testimonial: TestimonialItem) => void;
  editingItem?: TestimonialItem | null;
  hcc?: string;
  eventId?: string;
}

export default function AddEditTestimonialModal({
  isOpen,
  onClose,
  onSave,
  editingItem = null,
  hcc,
  eventId,
}: AddEditTestimonialModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { POST } = useApiRequest();

  // Set up file upload mutation
  const { mutateAsync: uploadImage, isPending: isUploading } = useMutation({
    mutationFn: async (file: File): Promise<UploadResponseImage[]> => {
      const formData = new FormData();
      formData.append("file", file);

      if (!hcc || !eventId) {
        throw new Error("HCC and Event ID are required for media upload");
      }

      return POST(
        `/api/healthcare-companies/${hcc}/e/events/${eventId}/media`,
        formData
      ) as Promise<UploadResponseImage[]>;
    },
  });

  const [formData, setFormData] = useState<{
    date: string;
    name: Record<string, string>;
    testimonial: Record<string, string>;
    video: string;
    image: ImageDetailType[];
    uploadedFile?: File;
  }>({
    date: "",
    name: {},
    testimonial: {},
    video: "",
    image: [],
  });
  const [errors, setErrors] = useState<{
    date?: string;
    nameEn?: string;
    nameBg?: string;
    testimonialEn?: string;
    testimonialBg?: string;
    media?: string;
  }>({});
  const [isNameValid, setIsNameValid] = useState(true);
  const [isTestimonialValid, setIsTestimonialValid] = useState(true);

  // Handle DropZone file drop
  const handleDropZoneDrop = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, uploadedFile: file }));
      setErrors((prev) => ({ ...prev, file: "" }));
    }
  };

  // Remove uploaded image
  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: [] }));
  };

  // Reset form when modal opens/closes or editing item changes
  useEffect(() => {
    if (isOpen) {
      if (editingItem) {
        // Handle date which can be a string or an object with date property
        const dateValue =
          typeof editingItem.date === "string"
            ? editingItem.date
            : editingItem.date &&
              typeof editingItem.date === "object" &&
              "date" in editingItem.date
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (editingItem.date as any).date.split(" ")[0] // Extract just the date part
            : "";

        setFormData({
          date: dateValue,
          name: {
            [i18n.language]: editingItem.name?.[i18n.language] || "",
          },
          testimonial: {
            [i18n.language]: editingItem.testimonial?.[i18n.language] || "",
          },
          video: editingItem.video || "",
          image: editingItem.image || [],
        });
      } else {
        setFormData({
          date: "",
          name: {},
          testimonial: {},
          video: "",
          image: [],
        });
      }
      setErrors({});
      setIsNameValid(true);
      setIsTestimonialValid(true);
    }
  }, [isOpen, editingItem]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.date.trim()) {
      newErrors.date = t("common.requiredField", {
        field_name: t("eventDetails.testimonials.modal.date"),
      });
    }

    // Check if at least one media type is provided
    if (!formData.image && !formData.video) {
      newErrors.media = t("eventDetails.testimonials.modal.mediaRequired");
    }

    setErrors(newErrors);
    // Return true only if there are no errors AND all language validations pass
    return Object.keys(newErrors).length === 0 && isNameValid && isTestimonialValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      // Show toast error when validation fails
      dispatch(
        showToast({
          message: t("eventDetails.testimonials.modal.error_missing_fields", {
            defaultValue: "Please fill in all required fields in both languages.",
          }),
          type: "error",
        })
      );
      return;
    }

    let finalImageUrl = formData.image;

    // Upload image if there's a file to upload
    if (formData.uploadedFile) {
      try {
        const response = await uploadImage(formData.uploadedFile);
        finalImageUrl = response;
        if (!finalImageUrl) {
          throw new Error("No image URL received from upload");
        }
      } catch (error) {
        console.error("Image upload failed:", error);
        return; // Prevent saving if upload fails
      }
    }

    const testimonialData: TestimonialItem = {
      date: formData.date,
      name: formData.name,
      testimonial: formData.testimonial,
      video: formData.video,
      image: finalImageUrl,
    };

    onSave(testimonialData);
    onClose();
  };

  const handleClose = () => {
    setFormData({
      date: "",
      name: {},
      testimonial: {},
      video: "",
      image: [], // 🔴 Add this to clear uploaded image
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={
        editingItem
          ? t("eventDetails.testimonials.modal.editTitle")
          : t("eventDetails.testimonials.modal.addTitle")
      }
      primaryAction={{
        content: editingItem
          ? t("eventDetails.testimonials.modal.editTitle")
          : t("eventDetails.testimonials.modal.saveButton"),
        onAction: handleSave,
        loading: isUploading,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleClose,
          disabled: isUploading,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Date Field */}
          <Box maxWidth="200px">
            <DatePickerField
              label={t("eventDetails.testimonials.modal.date")}
              value={formData.date}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, date: value }))
              }
              disabled={false}
            />
            {errors.date && (
              <Text as="p" variant="bodySm" tone="critical">
                {errors.date}
              </Text>
            )}
          </Box>

          {/* Attendee Name Fields */}
          <BlockStack gap="300">
            <GenerativeTextField
              id="attendee-name-bg"
              placementId="common.person_name"
              label={t("eventDetails.testimonials.modal.attendeeName")}
              value={formData.name}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  name: value,
                }))
              }
              type="textField"
              canGenerate={false}
              requiredIndicator
              validateAllLanguages={true}
              onValidationChange={setIsNameValid}
            />
          </BlockStack>

          {/* Image Upload Section */}
          <BlockStack gap="200">
            {/* DropZone for image upload */}
            <DropZone
              accept="image/png,image/jpg, image/jpeg, image/gif, image/webp, image/avif, image/heic, image/tiff, image/svg"
              type="image"
              onDrop={handleDropZoneDrop}
              allowMultiple={false}
            >
              {formData.image?.length > 0 || formData.uploadedFile ? (
                <Box
                  borderWidth="025"
                  borderColor="border"
                  borderRadius="200"
                  padding="400"
                  position="relative"
                >
                  <div style={{ textAlign: "center" }}>
                    <img
                      src={
                        formData.uploadedFile
                          ? URL.createObjectURL(formData.uploadedFile)
                          : (formData.image as ImageDetailType[])[0]?.src || ""
                      }
                      alt={t("eventDetails.testimonials.modal.avatarPreview")}
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        objectFit: "contain",
                        borderRadius: "4px",
                        border: "1px solid #e1e1e1",
                      }}
                    />
                  </div>
                  <div
                    style={{ position: "absolute", top: "8px", right: "8px" }}
                    onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                  >
                    <Button
                      icon={XIcon}
                      variant="plain"
                      tone="critical"
                      onClick={() => handleRemoveImage}
                      accessibilityLabel={t(
                        "eventDetails.testimonials.modal.removeImage"
                      )}
                    />
                  </div>
                </Box>
              ) : (
                <DropZone.FileUpload
                  actionTitle={t(
                    "eventDetails.testimonials.modal.uploadImageButton"
                  )}
                  actionHint={t(
                    "eventDetails.testimonials.modal.imageHelpText"
                  )}
                />
              )}
            </DropZone>
            {errors.media && (
              <Text as="p" variant="bodySm" tone="critical">
                {errors.media}
              </Text>
            )}
          </BlockStack>

          {/* Testimonial Text Fields */}
          <BlockStack gap="300">
            <GenerativeTextField
              id="testimonial-text-bg"
              placementId="event.event.testimonial.text"
              label={t("eventDetails.testimonials.modal.testimonialText")}
              value={{
                [i18n.language]: formData.testimonial[i18n.language],
              }}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  testimonial: value,
                }))
              }
              type="textArea"
              canGenerate={true}
              validateAllLanguages={true}
              onValidationChange={setIsTestimonialValid}
            />
          </BlockStack>

          {/* Video Link Field */}
          <BlockStack gap="200">
            <Text as="h3" variant="bodyMd">
              {t("eventDetails.testimonials.modal.videoLink")}
            </Text>

            <WebsiteField
              label=""
              value={formData.video}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, video: value }))
              }
              placeholder={t("common.placeholders.youtubeUrl")}
              helpText={t("eventDetails.testimonials.modal.videoHint")}
              requireHttps={false}
            />
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
